import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase/supabase';

const SupabaseAuthContext = createContext();

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  return context;
};

const CACHE_KEY = 'sohlocal_profile_cache';
const AUTH_STORAGE_KEY = 'sohlocal-auth-token';

export const SupabaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const mountedRef = useRef(true);

  const clearLocalAuth = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setUser(null);
    setProfile(null);
  }, []);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId || !mountedRef.current) return null;
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      if (data && mountedRef.current) {
        setProfile(data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        return data;
      }
      return null;
    } catch (err) {
      console.error('Profile fetch failure:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          clearLocalAuth();
        } else if (mountedRef.current) {
          if (session?.user) {
            setUser(session.user);
            await fetchProfile(session.user.id);
          } else {
            clearLocalAuth();
          }
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setIsInitialized(true);
        }
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mountedRef.current) return;
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setUser(session?.user ?? null);
        if (session?.user) await fetchProfile(session.user.id);
      } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        clearLocalAuth();
      }
      setLoading(false);
      setIsInitialized(true);
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile, clearLocalAuth]);

  const value = {
    user,
    profile,
    loading,
    isInitialized,
    refreshProfile: () => user ? fetchProfile(user.id) : null,
    signIn: (email, password, captchaToken) => supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken }
    }),
    signOut: async () => {
      try {
        clearLocalAuth();
        await supabase.auth.signOut();
      } finally {
        window.location.assign('/login');
      }
    },
    signUp: (email, password, metadata, captchaToken) => supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: window.location.origin,
        captchaToken
      }
    }),
    updateProfile: async (updates) => {
      if (!user) throw new Error("Authentication required");
      
      // CRITICAL: Filter out role-related fields from standard PATCH updates
      // This prevents the triggers from firing and throwing security errors
      // for standard name/phone updates.
      const { role, is_seller, id, created_at, email, ...safeUpdates } = updates;
      
      const { data, error } = await supabase
        .from('users')
        .update(safeUpdates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;
      if (mountedRef.current && data) {
        setProfile(data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      }
      return data;
    },
    becomeSeller: async () => {
      if (!user) throw new Error("Authentication required");
      // Call the RPC which now has the proper security bypass flag
      const { error } = await supabase.rpc('become_a_seller');
      if (error) throw error;
      return await fetchProfile(user.id);
    },
    resetPassword: (email, captchaToken) => supabase.auth.resetPasswordForEmail(email, {
      captchaToken
    }),
    verifyOtp: (email, token) => supabase.auth.verifyOtp({ email, token, type: 'recovery' }),
    updatePassword: (password) => supabase.auth.updateUser({ password })
  };

  return <SupabaseAuthContext.Provider value={value}>{children}</SupabaseAuthContext.Provider>;
};
